/**
 * TextAnim — 텍스트 등장 애니메이션 (메인·서브 공통)
 * data-text-anim="chars" | "slide-up"
 * data-text-anim-delay="0.5" (초 단위, 선택)
 * 전역: body 내 [data-text-anim] 대상
 */
const ATTR_ANIM = "data-text-anim";
const ATTR_DELAY = "data-text-anim-delay";
const CLASS_CHARS = "text-anim-chars";
const CLASS_CHAR = "text-anim__char";
const CLASS_LINE = "text-anim__line";
const CLASS_INVIEW = "is-inview";
const ROOT_MARGIN_BOTTOM_PX = 40; // observer rootMargin "0px 0px -40px 0px"와 동일

/** 새로고침 시 이미 뷰에 있는 텍스트 요소 즉시 활성화 여부. 기본 true */
export const TextAnimConfig = { ACTIVATE_WHEN_ALREADY_IN_VIEW: true };

function parseDelay(el) {
    const raw = el.getAttribute(ATTR_DELAY);
    if (raw == null || raw === "") return 0;
    const n = parseFloat(raw, 10);
    return Number.isFinite(n) ? n : 0;
}

/** HTML 엔티티 디코딩 (예: &amp; → &) — chars 애니메이션에서 그대로 노출되는 것 방지 */
function decodeHtmlEntities(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.innerHTML = str;
    return div.textContent || div.innerText || "";
}

/** 글자 단위로 분리 (줄 유지), 랜덤 딜레이 적용 */
function splitIntoChars(el) {
    if (el.classList.contains(CLASS_CHARS)) return;
    const baseDelay = parseDelay(el);
    const html = el.innerHTML || "";
    const lines = html
        .split(/<br\s*\/?>/i)
        .map((s) => decodeHtmlEntities(s.replace(/<[^>]+>/g, "").trim()));
    if (lines.length === 0 || (lines.length === 1 && !lines[0])) lines.push("");

    el.classList.add(CLASS_CHARS);
    el.innerHTML = "";

    lines.forEach((lineStr, lineIndex) => {
        const lineDiv = document.createElement("div");
        lineDiv.className = CLASS_LINE;
        lineDiv.setAttribute("aria-hidden", "true");

        [...lineStr].forEach((char, charIndex) => {
            const span = document.createElement("span");
            span.className = CLASS_CHAR;
            span.setAttribute("aria-hidden", "true");
            span.textContent = char;
            const stagger = charIndex * 0.032;
            const random = (Math.random() - 0.5) * 0.08;
            const delay = baseDelay + Math.max(0, stagger + random);
            span.style.setProperty("--char-delay", `${delay}s`);
            lineDiv.appendChild(span);
        });

        el.appendChild(lineDiv);
        // 줄 사이에 <br>을 넣지 않음 — .text-anim__line이 block이라 이미 줄바꿈되며,
        // br이 부모의 큰 line-height만큼 빈 공간을 만들어 과도한 간격이 생기는 문제 방지
    });
}

function initChars(scope) {
    scope.querySelectorAll(`[${ATTR_ANIM}="chars"]`).forEach((el) => splitIntoChars(el));
}

function initSlideUp(scope) {
    scope.querySelectorAll(`[${ATTR_ANIM}="slide-up"]`).forEach((el) => {
        const baseDelay = parseDelay(el);
        el.style.setProperty("--text-anim-delay", `${baseDelay}s`);
    });
}

/** observer와 동일한 영역: rootMargin -40px 하단 기준으로 인뷰 여부 */
function isInViewForText(el) {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight - ROOT_MARGIN_BOTTOM_PX;
}

function activateInViewElements(scope) {
    if (!TextAnimConfig.ACTIVATE_WHEN_ALREADY_IN_VIEW) return;
    const selector = `[${ATTR_ANIM}="chars"], [${ATTR_ANIM}="slide-up"]`;
    scope.querySelectorAll(selector).forEach((el) => {
        if (!el.classList.contains(CLASS_INVIEW) && isInViewForText(el)) {
            el.classList.add(CLASS_INVIEW);
        }
    });
}

function observeInview(scope) {
    const selector = `[${ATTR_ANIM}="chars"], [${ATTR_ANIM}="slide-up"]`;
    const elements = scope.querySelectorAll(selector);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add(CLASS_INVIEW);
                }
            });
        },
        { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    elements.forEach((el) => observer.observe(el));

    // 새로고침 시 이미 뷰에 있는 요소 즉시 is-inview 부여
    activateInViewElements(scope);
}

export function initTextAnim() {
    const scope = document.body;
    initChars(scope);
    initSlideUp(scope);
    observeInview(scope);
    // 레이아웃 안정 후 한 번 더 체크 (히어로 등 로드 지연 시 뷰 계산 보정)
    if (TextAnimConfig.ACTIVATE_WHEN_ALREADY_IN_VIEW) {
        setTimeout(() => activateInViewElements(scope), 150);
    }
}
